import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { getElevateUser, getSelectedUnit } from '../../../storage/Storage';
import { HitApi } from '../../../Api/ApiHit';
import { searchTask } from '../../../constant/Constant';
import { setPagination } from '../../../redux/Actions/TableAction';
import { monthWeeksOptions, weekDaysOptions } from '../../../constant/Options';
import PageHeader from '../../../PageHeader/PageHeader';

const WeeklyTaskCalendar = ({ selectedUserId }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const dispatch = useDispatch();
  const TableDataReducer = useSelector((state) => state.TableReducer);

  // Use selectedUserId if provided (for admin view), otherwise use logged-in user
  const userId = selectedUserId || getElevateUser()?._id;

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getWeeklyTasks = () => {
    setLoading(true);
    const json = {
      page: TableDataReducer.pagination.page,
      limit: 100,
      search: {
        assignedTo: { $in: [userId] },
        unitIds: getSelectedUnit()?._id,
        taskFrequency: "weekly"
      },
    };

    HitApi(json, searchTask).then((res) => {
      console.log("Weekly Tasks API Response:", res);
      const paginationData = {
        total: res?.data?.totalDocs || 0,
        page: TableDataReducer.pagination.page,
        limit: 100,
      };

      dispatch(setPagination(paginationData));
      if (res.statusCode === 200 && res.data?.docs) {
        setTasks(res?.data?.docs || []);
      } else {
        setError('Failed to load tasks');
      }
      setLoading(false);
    }).catch((err) => {
      console.error("API Error:", err);
      setError('Error loading tasks');
      setLoading(false);
    });
  };

  useEffect(() => {
    getWeeklyTasks();
  }, [selectedUserId]);

  // Helper functions
  const getDayLabel = (dayValue) => {
    const option = weekDaysOptions.find(opt => opt.value === parseInt(dayValue));
    return option ? option.label : '';
  };

  const getWeekLabel = (weekValue) => {
    const option = monthWeeksOptions.find(opt => opt.value === parseInt(weekValue));
    return option ? option.label : '';
  };

  const convertToJSDay = (yourDayNumber) => {
    const dayNum = parseInt(yourDayNumber);
    return dayNum === 7 ? 0 : dayNum;
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const getWeekOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfMonth = date.getDate();
    return Math.ceil((dayOfMonth + firstDay.getDay()) / 7);
  };

  const isLastWeekOfMonth = (date) => {
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const weekOfMonth = getWeekOfMonth(date);
    const lastWeekOfMonth = getWeekOfMonth(lastDay);
    return weekOfMonth === lastWeekOfMonth;
  };

  const getTasksForDate = (date) => {
    const jsDayOfWeek = date.getDay();
    const weekOfMonth = getWeekOfMonth(date);
    const isLastWeek = isLastWeekOfMonth(date);

    return tasks.filter(task => {
      const taskDays = task.weekDaysForMonthly?.map(day => parseInt(day)) || [];
      const taskWeeks = task.monthWeeks?.map(week => parseInt(week)) || [];

      const jsTaskDays = taskDays.map(convertToJSDay);

      const matchesDay = taskDays.length === 0 || jsTaskDays.includes(jsDayOfWeek);
      const matchesWeek = taskWeeks.length === 0 ||
        taskWeeks.includes(weekOfMonth) ||
        (taskWeeks.includes(5) && isLastWeek);

      return matchesDay && matchesWeek;
    });
  };

  const formatTime = (timeString) => {
    if (!timeString || !timeString.includes('-')) return timeString;
    const [startTime] = timeString.split('-');
    const [hours, minutes] = startTime.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 border-red-300 text-red-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'low': return 'bg-green-100 border-green-300 text-green-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <XCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const navigateWeek = (direction) => {
    setCurrentWeek(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction * 7));
      return newDate;
    });
  };

  const getWeekDays = () => {
    const startOfWeek = getStartOfWeek(currentWeek);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const dayNames = isMobile
    ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading weekly tasks...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <span className="block sm:inline">No Records Found</span>
      </div>
    );
  }

  const weekDays = getWeekDays();
  const today = new Date();

  return (
    <div className="mx-auto rounded-xl border">
      <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-3 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`font-bold flex items-center gap-2 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                <Calendar className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
                {isMobile ? 'Weekly Tasks' : 'Weekly Task Calendar'}
              </h1>
              <p className={`text-indigo-100 mt-1 ${isMobile ? 'text-sm' : ''}`}>
                {isMobile ? 'Weekly recurring tasks' : 'View and manage your weekly recurring tasks'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigateWeek(-1)}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors ${isMobile ? 'text-sm' : ''}`}
            >
              <ChevronLeft className="w-4 h-4" />
              {isMobile ? 'Prev' : 'Previous Week'}
            </button>

            <h2 className={`font-semibold text-gray-800 text-center ${isMobile ? 'text-sm' : 'text-xl'}`}>
              {isMobile ? (
                `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
              ) : (
                `${weekDays[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
              )}
            </h2>

            <button
              onClick={() => navigateWeek(1)}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors ${isMobile ? 'text-sm' : ''}`}
            >
              {isMobile ? 'Next' : 'Next Week'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Weekly Calendar */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4 sm:mb-6">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {weekDays.map((day, index) => (
            <div key={index} className="p-2 sm:p-4 text-center border-r border-gray-200 last:border-r-0">
              <div className={`font-semibold text-gray-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                {dayNames[day.getDay()]}
              </div>
              <div className={`font-bold mt-1 ${day.toDateString() === today.toDateString()
                ? 'text-indigo-600 bg-indigo-100 rounded-full flex items-center justify-center mx-auto'
                : 'text-gray-800'
                } ${isMobile ? 'text-sm w-6 h-6' : 'text-lg w-8 h-8'}`}>
                {day.getDate()}
              </div>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className={`grid grid-cols-7 ${isMobile ? 'min-h-[300px]' : 'min-h-[400px]'}`}>
          {weekDays.map((day, index) => {
            const dayTasks = getTasksForDate(day);
            const isToday = day.toDateString() === today.toDateString();
            const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();

            return (
              <div
                key={index}
                className={`border-r border-gray-200 last:border-r-0 p-1 sm:p-3 cursor-pointer transition-all duration-200 hover:bg-indigo-50 ${isToday ? 'bg-blue-50 border-blue-300' : ''
                  } ${isSelected ? 'bg-indigo-100 border-indigo-400 ring-2 ring-indigo-200' : ''}`}
                onClick={() => setSelectedDate(day)}
              >
                <div className="space-y-1 sm:space-y-2">
                  {dayTasks.slice(0, isMobile ? 2 : 4).map((task, taskIndex) => (
                    <div
                      key={`${task._id}-${taskIndex}`}
                      className={`p-1 sm:p-2 rounded border ${getPriorityColor(task.priority)} hover:shadow-sm transition-shadow ${isMobile ? 'text-xs' : 'text-xs'}`}
                      title={`${task.taskName} - ${task.description}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium truncate">{task.taskName}</span>
                        {!isMobile && getStatusIcon(task.taskIntervals?.[0]?.status)}
                      </div>
                      {!isMobile && task.taskIntervals?.[0]?.interval && (
                        <div className="text-xs opacity-75 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(task.taskIntervals[0].interval)}
                        </div>
                      )}
                      {isMobile && task.taskIntervals?.[0]?.interval && (
                        <div className="text-xs opacity-75">
                          {formatTime(task.taskIntervals[0].interval)}
                        </div>
                      )}
                    </div>
                  ))}
                  {dayTasks.length > (isMobile ? 2 : 4) && (
                    <div className="text-xs text-gray-500 font-medium bg-gray-100 p-1 rounded text-center">
                      +{dayTasks.length - (isMobile ? 2 : 4)} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task Details Panel */}
      {selectedDate && (
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <h3 className={`font-semibold text-gray-800 mb-4 flex items-center gap-2 ${isMobile ? 'text-base' : 'text-lg'}`}>
            <Calendar className={`text-indigo-600 ${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
            Tasks for {isMobile
              ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            }
          </h3>

          <div className="space-y-3 sm:space-y-4">
            {getTasksForDate(selectedDate).map((task, index) => (
              <div
                key={`${task._id}-detail-${index}`}
                className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-gray-800 flex items-center gap-2 ${isMobile ? 'text-sm' : ''}`}>
                      {task.priority === 'high' && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                      <span className="truncate">{task.taskName}</span>
                    </h4>
                    <p className={`text-gray-600 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>{task.description}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ml-2 ${getPriorityColor(task.priority)}`}>
                    {isMobile ? task.priority?.charAt(0).toUpperCase() : task.priority?.toUpperCase()}
                  </span>
                </div>

                {/* Schedule Information */}
                <div className={`text-gray-600 mb-3 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  <strong>Schedule:</strong>
                  {task.weekDaysForMonthly?.length > 0 && (
                    <span className="ml-1">
                      {task.weekDaysForMonthly.map(day => getDayLabel(day)).join(', ')}
                    </span>
                  )}
                  {task.monthWeeks?.length > 0 && (
                    <span className="ml-1">
                      ({task.monthWeeks.map(week => getWeekLabel(week)).join(', ')})
                    </span>
                  )}
                </div>

                {/* Time Intervals */}
                {task.taskIntervals?.length > 0 && (
                  <div className="mb-3">
                    <strong className={`text-gray-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>Time Slots:</strong>
                    <div className="flex flex-wrap gap-1 sm:gap-2 mt-1">
                      {task.taskIntervals.slice(0, isMobile ? 2 : task.taskIntervals.length).map((interval, idx) => (
                        <div key={idx} className="flex items-center gap-1 sm:gap-2 bg-gray-50 px-2 py-1 rounded">
                          <Clock className="w-3 h-3 text-gray-500" />
                          <span className={`font-mono ${isMobile ? 'text-xs' : 'text-sm'}`}>{interval.interval}</span>
                          {getStatusIcon(interval.status)}
                        </div>
                      ))}
                      {isMobile && task.taskIntervals.length > 2 && (
                        <div className="px-2 py-1 text-xs text-gray-500">
                          +{task.taskIntervals.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                )}


              </div>
            ))}

            {getTasksForDate(selectedDate).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className={isMobile ? 'text-sm' : ''}>No tasks scheduled for this date</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className={`grid gap-3 sm:gap-4 mt-4 sm:mt-6 ${isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-4'}`}>
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 text-center">
          <div className={`font-bold text-indigo-600 ${isMobile ? 'text-lg' : 'text-2xl'}`}>{tasks.length}</div>
          <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {isMobile ? 'Total Tasks' : 'Total Weekly Tasks'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 text-center">
          <div className={`font-bold text-red-600 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
            {tasks.filter(t => t.priority === 'high').length}
          </div>
          <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>High Priority</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 text-center">
          <div className={`font-bold text-green-600 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
            {tasks.reduce((acc, task) => acc + (task.taskIntervals?.filter(i => i.status === 'completed').length || 0), 0)}
          </div>
          <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {isMobile ? 'Completed' : 'Completed Intervals'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 text-center">
          <div className={`font-bold text-purple-600 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
            {tasks.reduce((acc, task) => acc + (task.taskIntervals?.length || 0), 0)}
          </div>
          <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {isMobile ? 'Time Slots' : 'Total Time Slots'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyTaskCalendar;